import FeatureAction from './FeatureAction';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Clock, AlertCircle, ChevronDown, ChevronUp, Download, RefreshCw, CheckCircle, Code, List } from 'lucide-react';
import { MarkerInfo, SearchResult, apiService, Index, PromptRun, VideoPromptRunResult } from '../services/api';
import { segmentCache, type SegmentData } from '../services/segmentCache';
import { mediaUrlService } from '../services/mediaUrlService';
import { processingRealtimeStore } from '../services/processingRealtimeStore';
import { resultRevisionKey } from '../utils/resultRevisions';
import { SegmentedPlayer } from './SegmentedPlayer';
import { SegmentedTimeline } from './SegmentedTimeline';
import { arePlaybackSegmentsEquivalent, prepareSegments, getTotalDuration, locateGlobalTime, VideoSegment } from '../utils/segmentedVideo';
import { formatSearchScore, getHighestFieldSearchScore, getImageSearchScore } from '../utils/searchScore';
import {
  getSearchResultIdentity,
  getSearchResultKey,
  getSearchResultReferenceSegmentId,
  getSearchResultRunId,
  getSearchResultVideoRunScopeKey,
} from '../utils/searchResultIdentity';
import { getResultDisplayDuration, getResultDisplayEndTime, getResultDisplayStartTime } from '../utils/resultTiming';
import { selectSearchResultMetadata } from '../utils/searchMetadata';
import {
  getSegmentPlaybackSource,
  normalizeGrantedMediaUrl,
  pickDurableMediaUri,
  pickMediaUri,
} from '../utils/mediaSources';
import { isResolvedVideoTitle } from '../utils/videoTitles';
import {
  PromptSemanticSearchFieldInstanceToken,
} from './prompt-schema/schemaFields';
import MarkerEditor from './MarkerEditor';
import GrantedPreviewImage from './GrantedPreviewImage';
import { SegmentPreviewGallery, SegmentPreviewGalleryItem } from './SegmentPreviewGallery';
import type { PreviewGrantProjection } from '../contexts/SearchWorkspaceContext';
import StructuredMetadataView, { getCollapsibleMetadataPaths } from './StructuredMetadataView';
import { downloadJsonFile } from '../utils/download';
import { resolveSegmentProcessingStatus } from '../utils/segmentStatus';
import { formatRunSegmentationLabel } from '../utils/runSegmentation';
import { useTabActivity } from '../contexts/TabActivityContext';
import { useResolvedMediaUrl } from '../hooks/useResolvedMediaUrl';
import SegmentDownloadActions from './SegmentDownloadActions';
import type { SearchResultFullVideoState as FullVideoState } from '../features/search-workspace/types';
import ResultDetailSidebar from './ResultDetailSidebar';
import MetadataViewControls, { MetadataValueView } from './MetadataViewControls';
import { getBestFieldScoreForDisplayPath } from '../utils/searchFieldScores';
import { createSearchFieldMatcher } from '../utils/searchFieldMatches';
import './SearchResultDetailModal.css';
export { getBestFieldScoreForDisplayPath } from '../utils/searchFieldScores';

type MetadataSortType = 'score' | 'alpha';
type SortDirection = 'asc' | 'desc';
type MetadataViewMode = 'fields' | 'json';

const WORKSPACE_PLAYBACK_PERSIST_INTERVAL_MS = 850;
const EMPTY_PRIORITIZED_FIELD_PATHS: string[] = [];

const normalizePriorityFieldPathSegments = (fieldPath: string): string[] => {
  const normalizedPath = String(fieldPath || '').trim();
  if (!normalizedPath) return [];

  if (normalizedPath.startsWith('__nested__:')) {
    try {
      const decoded = JSON.parse(normalizedPath.slice('__nested__:'.length));
      if (Array.isArray(decoded)) {
        return decoded.flatMap((segment): string[] => {
          if (typeof segment !== 'string') return [];
          const normalizedSegment = segment.trim().replace(/\[\]$/, '');
          return normalizedSegment ? [normalizedSegment] : [];
        });
      }
    } catch {
      return [];
    }
  }

  return normalizedPath
    .replace(/\[(?:\d+)?\]/g, '')
    .split('.')
    .map((segment) => segment.trim())
    .filter(Boolean);
};

const getFieldPriorityRank = (
  fieldPathTokens: PromptSemanticSearchFieldInstanceToken[],
  prioritizedFieldPathSegments: string[][],
): number => {
  const displaySegments = fieldPathTokens
    .filter((token): token is string => typeof token === 'string')
    .map((segment) => segment.trim().replace(/\[\]$/, ''))
    .filter(Boolean);
  if (displaySegments.length === 0) return Number.MAX_SAFE_INTEGER;

  const isPrefix = (left: string[], right: string[]): boolean => (
    left.length <= right.length
    && left.every((segment, index) => segment === right[index])
  );
  const priorityIndex = prioritizedFieldPathSegments.findIndex((targetSegments) => {
    return targetSegments.length > 0
      && (isPrefix(displaySegments, targetSegments) || isPrefix(targetSegments, displaySegments));
  });
  return priorityIndex === -1 ? Number.MAX_SAFE_INTEGER : priorityIndex;
};

const normalizeSearchVideoSegment = (segment: SegmentData): SegmentData => {
  const segmentUri = pickMediaUri(segment.segment_uri, segment.gcs_uri);
  const thumbnailUri = normalizeGrantedMediaUrl(segment.thumbnail_uri);
  const gifUri = normalizeGrantedMediaUrl(segment.gif_uri);
  const processed = segment.segment_status
    ? segment.segment_status === 'successful'
    : Boolean(segment.processed);
  const processingFailed = segment.segment_status
    ? segment.segment_status === 'failed'
    : Boolean(segment.processing_failed);

  return {
    ...segment,
    processed,
    processing_failed: processingFailed,
    error_message: segment.failure_message || segment.error_message,
    segment_uri: segmentUri || undefined,
    thumbnail_uri: thumbnailUri || undefined,
    gif_uri: gifUri || undefined,
    thumbnail_available: Boolean(segment.thumbnail_gcs_uri || thumbnailUri),
    gif_available: Boolean(segment.gif_gcs_uri || gifUri),
  };
};

const normalizeSearchVideoSegments = (segments: SegmentData[] = []): SegmentData[] => (
  segments.map((segment) => normalizeSearchVideoSegment(segment))
);

const resolvePreferredRunId = (
  restoredRunId: string | null | undefined,
  resultRunId: string | null | undefined,
  runs: PromptRun[],
): string | null => {
  const runIds = new Set(
    runs
      .map((run) => run.run_id)
      .filter((runId): runId is string => Boolean(runId)),
  );

  if (restoredRunId && runIds.has(restoredRunId)) {
    return restoredRunId;
  }
  if (resultRunId) {
    return resultRunId;
  }
  return runs[0]?.run_id || null;
};

const getRunDisplayName = (runId: string | null, runs: PromptRun[]): string => {
  if (!runId) {
    return 'Select an extraction run';
  }
  const run = runs.find((candidate) => candidate.run_id === runId);
  if (run) {
    return run.prompt_name;
  }
  const shortRunId = runId.length > 8 ? `${runId.slice(0, 8)}...` : runId;
  return `Current result run (${shortRunId})`;
};

const SegmentGifPreview: React.FC<{
  segment: SegmentData;
  videoId: string;
}> = ({ segment, videoId }) => {
  const thumbnailGcsUri = pickDurableMediaUri(segment.thumbnail_gcs_uri);
  const gifGcsUri = pickDurableMediaUri(segment.gif_gcs_uri);
  const projection: PreviewGrantProjection | undefined = thumbnailGcsUri || gifGcsUri
    ? {
        identity: {
          kind: 'segment',
          video_id: videoId,
          segment_id: segment.segment_id,
        },
        thumbnailGcsUri,
        gifGcsUri,
      }
    : undefined;
  return (
    <GrantedPreviewImage
      posterUrl={segment.thumbnail_uri}
      animatedUrl={segment.gif_uri}
      projection={projection}
      alt={`Segment ${segment.segment_id} preview`}
      className="segment-gif"
      containerClassName="granted-segment-preview"
      engaged
      placeholder={(
        <div className="gif-placeholder-mini">
      <div className="gif-status">
        {segment.processing_failed ? (
          <AlertCircle size={12} />
        ) : segment.processed ? (
          <CheckCircle size={12} />
        ) : (
          <Clock size={12} />
        )}
      </div>
        </div>
      )}
    />
  );
};

// Helper to detect if content is an image from URI
// Handles signed URLs with query parameters (e.g., file.jpg?X-Goog-...)
const isImageFile = (uri: string): boolean => {
  // Remove query params and fragments before checking extension
  const pathPart = uri.split('?')[0].split('#')[0];
  const ext = pathPart.toLowerCase().split('.').pop() || '';
  return ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'tiff', 'heic', 'heif'].includes(ext);
};

type FullVideoSnapshot = Pick<
  FullVideoState,
  | 'selectedRunId'
  | 'videoSegments'
  | 'availableRuns'
  | 'selectedVideoSegmentId'
  | 'globalTime'
  | 'isPlaying'
  | 'videoDuration'
>;

interface SearchResultDetailModalProps {
  result: SearchResult | null;
  isOpen: boolean;
  isVisible?: boolean;
  onClose: () => void;
  indexes?: Index[];
  indexRuns?: Record<string, PromptRun[]>;
  allSearchResults?: SearchResult[];
  fullVideoState?: FullVideoState | null;
  onFullVideoStateChange?: (state: FullVideoState | null) => void;
  prioritizedFieldPaths?: string[];
}

const SearchResultDetailModal: React.FC<SearchResultDetailModalProps> = ({
  result,
  isOpen,
  isVisible = true,
  onClose,
  indexes = [],
  indexRuns = {},
  allSearchResults = [],
  fullVideoState,
  onFullVideoStateChange,
  prioritizedFieldPaths = EMPTY_PRIORITIZED_FIELD_PATHS,
}) => {
  const { isActive } = useTabActivity();
  const isPresentationVisible = isActive && isOpen && isVisible;
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = localStorage.getItem('searchSidebarWidth');
    const parsed = saved ? Number.parseInt(saved, 10) : Number.NaN;
    return Number.isFinite(parsed) ? Math.max(400, Math.min(1400, parsed)) : 900;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [videoTitle, setVideoTitle] = useState<string>('');
  const currentResultKey = result ? getSearchResultKey(result) : null;
  const currentModalResultKey = result && currentResultKey
    ? `${result.result_type}:${currentResultKey}`
    : null;
  const currentResultIdentity = result ? getSearchResultIdentity(result) : null;
  const currentReferenceSegmentId = result ? getSearchResultReferenceSegmentId(result) : null;
  const currentResultRunId = result ? getSearchResultRunId(result) : null;
  const currentVideoRunScopeKey = result
    ? getSearchResultVideoRunScopeKey(result.video_id, currentResultRunId)
    : null;
  const persistedStateMatchesResult = Boolean(
    fullVideoState
    && result
    && fullVideoState.videoId === result.video_id
    // Legacy snapshots did not distinguish the result's immutable run scope
    // from a user-selected alternate run. Fail closed for those snapshots so
    // stale state cannot silently cross a search-result scope.
    && fullVideoState.originatingRunId !== undefined
    && fullVideoState.originatingRunId === currentResultRunId
    && (
      fullVideoState.resultIdentity === currentResultIdentity
      || (
        fullVideoState.videoRunScopeKey === currentVideoRunScopeKey
        && fullVideoState.originatingResultKey === currentResultKey
      )
    )
  );

  const resultPlaybackSource = getSegmentPlaybackSource(result || {});
  useEffect(() => {
    if (
      !isPresentationVisible
      || persistedStateMatchesResult
      || !result?.video_id
      || !currentReferenceSegmentId
      || !mediaUrlService.needsResolution(resultPlaybackSource)
    ) return;

    // Authorize only the clicked segment in parallel with its metadata load.
    // The player joins this existing single-flight cache; no second URL owner
    // or speculative signing of the rest of the video is needed.
    void mediaUrlService.resolveUrl(resultPlaybackSource, {
      videoId: result.video_id,
      segmentId: currentReferenceSegmentId,
    }).catch(() => {
      // The mounted player owns actionable errors and its normal retry UI.
    });
  }, [
    currentReferenceSegmentId,
    isPresentationVisible,
    persistedStateMatchesResult,
    result?.video_id,
    resultPlaybackSource,
  ]);

  // Metadata display controls
  const [metadataSortBy, setMetadataSortBy] = useState<MetadataSortType>('score');
  const [metadataSortDir, setMetadataSortDir] = useState<SortDirection>('desc');
  const [metadataViewMode, setMetadataViewMode] = useState<MetadataViewMode>('fields');
  const [collapsedMetadataPaths, setCollapsedMetadataPaths] = useState<Set<string>>(new Set());
  const allFieldsExpanded = collapsedMetadataPaths.size === 0;
  const prioritizedMetadataFieldPathSegments = useMemo(() => (
    prioritizedFieldPaths
      .map(normalizePriorityFieldPathSegments)
      .filter((segments) => segments.length > 0)
  ), [prioritizedFieldPaths]);

  // Initialize the single full-video workspace from its matching persisted scope.
  const [videoSegments, setVideoSegments] = useState<SegmentData[]>(() => {
    if (persistedStateMatchesResult && fullVideoState) {
      return normalizeSearchVideoSegments(fullVideoState.videoSegments || []);
    }
    return [];
  });
  const [preparedSegments, setPreparedSegments] = useState<VideoSegment[]>(() => {
    if (persistedStateMatchesResult && fullVideoState && fullVideoState.videoSegments?.length > 0) {
      return prepareSegments(
        normalizeSearchVideoSegments(fullVideoState.videoSegments),
        result?.video_id,
      );
    }
    return [];
  });
  const [selectedVideoSegment, setSelectedVideoSegment] = useState<SegmentData | null>(() => {
    if (persistedStateMatchesResult && fullVideoState && fullVideoState.videoSegments?.length > 0) {
      return normalizeSearchVideoSegments(fullVideoState.videoSegments)
        .find((s: SegmentData) => s.segment_id === fullVideoState.selectedVideoSegmentId) || null;
    }
    return null;
  });
  const [availableRuns, setAvailableRuns] = useState<PromptRun[]>(() => {
    if (persistedStateMatchesResult && fullVideoState) {
      return fullVideoState.availableRuns || [];
    }
    return [];
  });
  const [selectedRunId, setSelectedRunId] = useState<string | null>(() => {
    if (persistedStateMatchesResult && fullVideoState) {
      return fullVideoState.selectedRunId;
    }
    return null;
  });
  const [globalTime, setGlobalTime] = useState<number>(() => {
    if (persistedStateMatchesResult && fullVideoState) {
      return fullVideoState.globalTime || 0;
    }
    return 0;
  });
  const [isPlaying, setIsPlaying] = useState<boolean>(false); // Always start paused
  const [videoDuration, setVideoDuration] = useState<number>(() => {
    if (persistedStateMatchesResult && fullVideoState) {
      return fullVideoState.videoDuration || 0;
    }
    return 0;
  });
  const [isLoadingVideoSegments, setIsLoadingVideoSegments] = useState(false);
  const [videoSegmentsError, setVideoSegmentsError] = useState<string | null>(null);
  const [promptRunsError, setPromptRunsError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [fullVideoReloadNonce, setFullVideoReloadNonce] = useState(0);
  const [showRunDropdown, setShowRunDropdown] = useState(false);
  const [showMatchingSegmentsOnly, setShowMatchingSegmentsOnly] = useState(true);

  useEffect(() => {
    if (isOpen) setShowMatchingSegmentsOnly(true);
  }, [currentModalResultKey, isOpen]);

  // Video-level output state
  const persistedVideoLevelState = persistedStateMatchesResult
    && fullVideoState
    && fullVideoState.videoLevelResultRunId === fullVideoState.selectedRunId
    ? {
        result: fullVideoState.videoLevelResult || null,
        runId: fullVideoState.videoLevelResultRunId || null,
        error: fullVideoState.videoLevelResultError || null,
        loaded: Boolean(fullVideoState.videoLevelResultLoaded),
      }
    : { result: null, runId: null, error: null, loaded: false };
  const [videoLevelResult, setVideoLevelResult] = useState<VideoPromptRunResult | null>(
    persistedVideoLevelState.result,
  );
  const [isLoadingVideoLevelResult, setIsLoadingVideoLevelResult] = useState(false);
  const [videoLevelRevision, setVideoLevelRevision] = useState(0);
  const [videoLevelResultError, setVideoLevelResultError] = useState<string | null>(
    persistedVideoLevelState.error,
  );
  const [videoLevelViewMode, setVideoLevelViewMode] = useState<MetadataViewMode>('fields');
  const [collapsedVideoLevelPaths, setCollapsedVideoLevelPaths] = useState<Set<string>>(new Set());

  // Track if we've already loaded from cache for this video
  const loadedFromCacheRef = useRef<string | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isPresentationVisible) return;
    setIsPlaying(false);
    setIsResizing(false);
  }, [currentModalResultKey, isPresentationVisible]);
  const resizeStartX = useRef<number>(0);
  const resizeStartWidth = useRef<number>(0);
  const sidebarWidthRef = useRef(sidebarWidth);
  const videoLevelSnapshotRef = useRef(persistedVideoLevelState);
  const fullVideoFieldsRef = useRef({
    selectedRunId,
    videoSegments,
    availableRuns,
    selectedVideoSegmentId: selectedVideoSegment?.segment_id || null,
    globalTime,
    isPlaying,
    videoDuration,
  });
  const fullVideoLoadTokenRef = useRef(0);
  const videoTitleLoadTokenRef = useRef(0);
  const videoLevelLoadTokenRef = useRef(0);
  const fullVideoPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFullVideoSnapshotRef = useRef<FullVideoSnapshot | null>(null);
  const lastPublishedFullVideoSnapshotRef = useRef<FullVideoSnapshot | null>(null);
  const lastFullVideoPersistAtRef = useRef(0);
  const prevRunIdRef = useRef<string | null>(null);
  const selectedRunIdRef = useRef<string | null>(selectedRunId);
  const onFullVideoStateChangeRef = useRef(onFullVideoStateChange);
  const runDropdownRef = useRef<HTMLDivElement>(null);
  const persistedSnapshotRef = useRef<FullVideoState | null>(null);
  persistedSnapshotRef.current = persistedStateMatchesResult ? fullVideoState || null : null;

  fullVideoFieldsRef.current = {
    selectedRunId,
    videoSegments,
    availableRuns,
    selectedVideoSegmentId: selectedVideoSegment?.segment_id || null,
    globalTime,
    isPlaying,
    videoDuration,
  };

  useEffect(() => {
    onFullVideoStateChangeRef.current = onFullVideoStateChange;
  }, [onFullVideoStateChange]);

  const displayStartTime = getResultDisplayStartTime(result);
  const displayEndTime = getResultDisplayEndTime(result);
  const displayDuration = getResultDisplayDuration(result);
  const indexIdForFieldMarkers = result?.source_index_id;

  const persistFullVideoSnapshot = useCallback((snapshot: FullVideoSnapshot) => {
    if (onFullVideoStateChangeRef.current && result?.video_id) {
      onFullVideoStateChangeRef.current({
        resultIdentity: currentResultIdentity,
        videoId: result.video_id,
        videoRunScopeKey: currentVideoRunScopeKey,
        originatingResultKey: currentResultKey,
        originatingRunId: currentResultRunId,
        selectedRunId: snapshot.selectedRunId,
        videoSegments: snapshot.videoSegments,
        availableRuns: snapshot.availableRuns,
        selectedVideoSegmentId: snapshot.selectedVideoSegmentId,
        globalTime: snapshot.globalTime,
        isPlaying: snapshot.isPlaying,
        videoDuration: snapshot.videoDuration,
        videoLevelResult: videoLevelSnapshotRef.current.result,
        videoLevelResultRunId: videoLevelSnapshotRef.current.runId,
        videoLevelResultError: videoLevelSnapshotRef.current.error,
        videoLevelResultLoaded: videoLevelSnapshotRef.current.loaded,
      });
    }
  }, [currentResultIdentity, currentResultKey, currentResultRunId, currentVideoRunScopeKey, result?.video_id]);

  const publishVideoLevelSnapshot = useCallback((snapshot: {
    result: VideoPromptRunResult | null;
    runId: string | null;
    error: string | null;
    loaded: boolean;
  }) => {
    videoLevelSnapshotRef.current = snapshot;
    persistFullVideoSnapshot(fullVideoFieldsRef.current);
  }, [persistFullVideoSnapshot]);

  const publishPendingFullVideoSnapshot = useCallback((forceCurrent = false) => {
    if (fullVideoPersistTimerRef.current !== null) {
      clearTimeout(fullVideoPersistTimerRef.current);
      fullVideoPersistTimerRef.current = null;
    }
    if (forceCurrent) {
      pendingFullVideoSnapshotRef.current = { ...fullVideoFieldsRef.current };
    }
    const snapshot = pendingFullVideoSnapshotRef.current;
    pendingFullVideoSnapshotRef.current = null;
    if (!snapshot) return;
    const previous = lastPublishedFullVideoSnapshotRef.current;
    if (
      previous
      && previous.selectedRunId === snapshot.selectedRunId
      && previous.videoSegments === snapshot.videoSegments
      && previous.availableRuns === snapshot.availableRuns
      && previous.selectedVideoSegmentId === snapshot.selectedVideoSegmentId
      && previous.globalTime === snapshot.globalTime
      && previous.isPlaying === snapshot.isPlaying
      && previous.videoDuration === snapshot.videoDuration
    ) return;
    lastPublishedFullVideoSnapshotRef.current = snapshot;
    lastFullVideoPersistAtRef.current = Date.now();
    persistFullVideoSnapshot(snapshot);
  }, [persistFullVideoSnapshot]);

  const queueFullVideoSnapshot = useCallback((snapshot: FullVideoSnapshot) => {
    pendingFullVideoSnapshotRef.current = { ...snapshot };
    const elapsed = Date.now() - lastFullVideoPersistAtRef.current;
    if (
      lastFullVideoPersistAtRef.current === 0
      || elapsed >= WORKSPACE_PLAYBACK_PERSIST_INTERVAL_MS
    ) {
      publishPendingFullVideoSnapshot();
      return;
    }
    if (fullVideoPersistTimerRef.current === null) {
      fullVideoPersistTimerRef.current = setTimeout(
        () => publishPendingFullVideoSnapshot(),
        Math.max(0, WORKSPACE_PLAYBACK_PERSIST_INTERVAL_MS - elapsed),
      );
    }
  }, [publishPendingFullVideoSnapshot]);

  const handleFullVideoTimeChange = useCallback((nextGlobalTime: number) => {
    setGlobalTime(nextGlobalTime);
    const snapshot = { ...fullVideoFieldsRef.current, globalTime: nextGlobalTime };
    fullVideoFieldsRef.current = snapshot;
    queueFullVideoSnapshot(snapshot);
  }, [queueFullVideoSnapshot]);

  const handleFullVideoPlayingChange = useCallback((nextIsPlaying: boolean) => {
    setIsPlaying(nextIsPlaying);
    fullVideoFieldsRef.current = { ...fullVideoFieldsRef.current, isPlaying: nextIsPlaying };
    if (nextIsPlaying) {
      queueFullVideoSnapshot(fullVideoFieldsRef.current);
    } else {
      publishPendingFullVideoSnapshot(true);
    }
  }, [publishPendingFullVideoSnapshot, queueFullVideoSnapshot]);

  useEffect(() => () => {
    publishPendingFullVideoSnapshot();
    lastPublishedFullVideoSnapshotRef.current = null;
    lastFullVideoPersistAtRef.current = 0;
  }, [currentModalResultKey, publishPendingFullVideoSnapshot]);

  // Get all search results from the same video
  const searchResultsInVideo = useMemo(() => {
    if (!result) return [];
    const candidates = allSearchResults.some(candidate => getSearchResultIdentity(candidate) === currentResultIdentity)
      ? allSearchResults
      : [...allSearchResults, result];
    return candidates.filter(r => r.video_id === result.video_id && r.result_type !== 'video' && !!r.segment_id);
  }, [result, allSearchResults, currentResultIdentity]);

  // Count unique matching segments rather than duplicate search occurrences.
  const searchResultsByRunId = useMemo(() => {
    const matches = new Map<string, Set<string>>();
    searchResultsInVideo.forEach(r => {
      const runId = getSearchResultRunId(r);
      if (runId && r.segment_id) {
        const ids = matches.get(runId) || new Set<string>();
        ids.add(r.segment_id);
        matches.set(runId, ids);
      }
    });
    return new Map(Array.from(matches, ([runId, ids]) => [runId, ids.size]));
  }, [searchResultsInVideo]);

  // Get search results filtered by the currently selected run (for video view)
  const searchResultsForCurrentRun = useMemo(() => {
    if (!selectedRunId) return [];
    return searchResultsInVideo.filter(r => {
      const runId = getSearchResultRunId(r);
      return runId === selectedRunId;
    });
  }, [searchResultsInVideo, selectedRunId]);

  // Create a map of segment_id to search result for current run only (for video view)
  const searchResultsBySegmentIdForCurrentRun = useMemo(() => {
    return new Map<string, SearchResult>(
      searchResultsForCurrentRun
        .filter((r): r is SearchResult & { segment_id: string } => typeof r.segment_id === 'string' && r.segment_id.length > 0)
        .map(r => [r.segment_id, r])
    );
  }, [searchResultsForCurrentRun]);

  const highestFieldScoresBySegmentId = useMemo(() => {
    const scores = new Map<string, number>();
    searchResultsForCurrentRun.forEach((hit) => {
      const score = getHighestFieldSearchScore(hit);
      if (hit.segment_id && score !== null) {
        scores.set(hit.segment_id, Math.max(scores.get(hit.segment_id) ?? -Infinity, score));
      }
    });
    return scores;
  }, [searchResultsForCurrentRun]);

  const matchingVideoSegments = useMemo(() => videoSegments.filter(
    (segment) => searchResultsBySegmentIdForCurrentRun.has(segment.segment_id),
  ), [videoSegments, searchResultsBySegmentIdForCurrentRun]);
  const displayedVideoSegments = showMatchingSegmentsOnly ? matchingVideoSegments : videoSegments;

  // Get count of search results for a specific run
  const getRunResultCount = (runId: string): number => {
    return searchResultsByRunId.get(runId) || 0;
  };

  const buildLoadedVideoSegmentState = useCallback((
    segments: SegmentData[],
    preferredSegmentId: string | null,
  ) => {
    const nextSegments = segments || [];
    const nextPreparedSegments = prepareSegments(nextSegments, result?.video_id);
    const nextVideoDuration = nextSegments.length > 0
      ? Math.max(...nextSegments.map((segment) => Number(segment.end_time) || 0))
      : 0;

    const preferredSegment = preferredSegmentId
      ? nextSegments.find((segment) => segment.segment_id === preferredSegmentId) || null
      : null;
    const preferredPreparedSegment = preferredSegment
      ? nextPreparedSegments.find((segment) => segment.id === preferredSegment.segment_id) || null
      : null;
    const fallbackSegment = nextSegments[0] || null;
    const fallbackPreparedSegment = fallbackSegment
      ? nextPreparedSegments.find((segment) => segment.id === fallbackSegment.segment_id) || null
      : null;
    const nextSelectedSegment = preferredPreparedSegment ? preferredSegment : fallbackSegment;
    const nextGlobalTime = preferredPreparedSegment?.start ?? fallbackPreparedSegment?.start ?? 0;

    return {
      videoSegments: nextSegments,
      preparedSegments: nextPreparedSegments,
      videoDuration: nextVideoDuration,
      selectedVideoSegment: nextSelectedSegment,
      globalTime: nextGlobalTime,
    };
  }, [result?.video_id]);

  const applyLoadedVideoSegmentState = useCallback((
    segments: SegmentData[],
    preferredSegmentId: string | null,
  ) => {
    const nextState = buildLoadedVideoSegmentState(segments, preferredSegmentId);
    setVideoSegments(nextState.videoSegments);
    setPreparedSegments(nextState.preparedSegments);
    setVideoDuration(nextState.videoDuration);
    setSelectedVideoSegment(nextState.selectedVideoSegment);
    setGlobalTime(nextState.globalTime);
    setIsPlaying(false);
    return nextState;
  }, [buildLoadedVideoSegmentState]);

  // Get search result for a segment in video mode (for field scores)
  const getSearchResultForSegment = useCallback((segmentId: string): SearchResult | undefined => {
    return searchResultsBySegmentIdForCurrentRun.get(segmentId);
  }, [searchResultsBySegmentIdForCurrentRun]);

  const selectedSegmentSearchResult = selectedVideoSegment
    ? getSearchResultForSegment(selectedVideoSegment.segment_id)
    : undefined;
  const segmentFieldMatcher = useMemo(() => createSearchFieldMatcher(selectedSegmentSearchResult), [selectedSegmentSearchResult]);
  const selectedVideoSearchResult = useMemo(() => {
    const matchesScope = (hit: SearchResult) => hit.result_type === 'video'
      && hit.video_id === result?.video_id && getSearchResultRunId(hit) === selectedRunId;
    return result && matchesScope(result) ? result : allSearchResults.find(matchesScope);
  }, [result, allSearchResults, selectedRunId]);
  const videoFieldMatcher = useMemo(() => createSearchFieldMatcher(selectedVideoSearchResult), [selectedVideoSearchResult]);
  const selectedResultMetadata = selectSearchResultMetadata(
    selectedSegmentSearchResult?.extracted_metadata,
    selectedSegmentSearchResult?.metadata,
  );
  const selectedSegmentMetadata = Object.keys(selectedResultMetadata).length > 0
    ? selectedResultMetadata
    : selectSearchResultMetadata(selectedVideoSegment?.metadata, undefined);

  // Toggle sort direction or change sort type
  const handleSortChange = (sortType: MetadataSortType) => {
    if (metadataSortBy === sortType) {
      setMetadataSortDir(metadataSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setMetadataSortBy(sortType);
      setMetadataSortDir(sortType === 'score' ? 'desc' : 'asc');
    }
  };

  // Toggle all fields expanded/collapsed
  const handleToggleAllFields = (targetMetadata: Record<string, unknown>) => {
    setCollapsedMetadataPaths((currentPaths) => (
      currentPaths.size === 0
        ? new Set(getCollapsibleMetadataPaths(targetMetadata))
        : new Set()
    ));
  };

  // Toggle nested path expansion
  const toggleMetadataPath = useCallback((path: string) => {
    setCollapsedMetadataPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const handlePromptRunMarkerChange = useCallback(
    (runId: string, nextMarker: MarkerInfo) => {
      if (!result) return;
      setAvailableRuns((prev) => prev.map((r) => (r.run_id === runId ? { ...r, marker: nextMarker } : r)));
      segmentCache
        .patchCachedPromptRun(result.video_id, runId, { marker: nextMarker })
        .catch((err) => console.warn('Failed to patch cached prompt run marker:', err));
    },
    [result]
  );

  // Get index info from result
  const getIndexInfo = (): { name: string; id: string } | null => {
    if (!result?.source_index_id) return null;
    const index = indexes.find(idx => idx.index_id === result.source_index_id);
    return index ? { name: index.name, id: index.index_id } : null;
  };

  const indexInfo = result ? getIndexInfo() : null;

  // Close run dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (runDropdownRef.current && !runDropdownRef.current.contains(event.target as Node)) {
        setShowRunDropdown(false);
      }
    };

    if (showRunDropdown && isPresentationVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPresentationVisible, showRunDropdown]);

  // Persist state when modal closes (for tab switching)
  useEffect(() => {
    if (!isOpen && result) {
      publishPendingFullVideoSnapshot(true);
    }
  }, [
    isOpen,
    publishPendingFullVideoSnapshot,
    result,
  ]);

  // Restore state when opening modal with same video, or reset for different video
  useEffect(() => {
    if (isOpen && result) {
      if (persistedStateMatchesResult && fullVideoState) {
        // Restore from persisted state
        if (loadedFromCacheRef.current !== currentModalResultKey) {
          const restoredSegments = normalizeSearchVideoSegments(fullVideoState.videoSegments || []);
          setVideoSegments(restoredSegments);
          setPreparedSegments(
            restoredSegments.length > 0
              ? prepareSegments(restoredSegments, result.video_id)
              : [],
          );
          setSelectedVideoSegment(restoredSegments.find((s: SegmentData) => s.segment_id === fullVideoState.selectedVideoSegmentId) || null);
          setAvailableRuns(fullVideoState.availableRuns || []);
          selectedRunIdRef.current = fullVideoState.selectedRunId;
          setSelectedRunId(fullVideoState.selectedRunId);
          setGlobalTime(fullVideoState.globalTime || 0);
          setVideoDuration(fullVideoState.videoDuration || 0);
          setIsPlaying(false); // Always start paused
          setVideoSegmentsError(null);
          const restoredVideoLevelState = fullVideoState.videoLevelResultRunId === fullVideoState.selectedRunId
            ? {
                result: fullVideoState.videoLevelResult || null,
                runId: fullVideoState.videoLevelResultRunId || null,
                error: fullVideoState.videoLevelResultError || null,
                loaded: Boolean(fullVideoState.videoLevelResultLoaded),
              }
            : { result: null, runId: null, error: null, loaded: false };
          videoLevelSnapshotRef.current = restoredVideoLevelState;
          setVideoLevelResult(restoredVideoLevelState.result);
          setVideoLevelResultError(restoredVideoLevelState.error);
          setIsLoadingVideoLevelResult(false);
          loadedFromCacheRef.current = currentModalResultKey;
        }
      } else if (loadedFromCacheRef.current !== currentModalResultKey) {
        // Different video, reset state
        fullVideoLoadTokenRef.current += 1;
        prevRunIdRef.current = null;
        setVideoSegments([]);
        setPreparedSegments([]);
        setSelectedVideoSegment(null);
        setAvailableRuns([]);
        selectedRunIdRef.current = null;
        setSelectedRunId(null);
        setGlobalTime(0);
        setIsPlaying(false);
        setVideoDuration(0);
        setVideoSegmentsError(null);
        videoLevelLoadTokenRef.current += 1;
        videoLevelSnapshotRef.current = { result: null, runId: null, error: null, loaded: false };
        setVideoLevelResult(null);
        setVideoLevelResultError(null);
        setIsLoadingVideoLevelResult(false);
        loadedFromCacheRef.current = currentModalResultKey;
      }
    }
  }, [currentModalResultKey, currentReferenceSegmentId, fullVideoState, isOpen, persistedStateMatchesResult, result]);

  useEffect(() => {
    selectedRunIdRef.current = selectedRunId;
  }, [selectedRunId]);

  const handleRetryFullVideoLoad = useCallback(() => {
    fullVideoLoadTokenRef.current += 1;
    prevRunIdRef.current = null;
    setVideoSegmentsError(null);
    setPromptRunsError(null);
    setFullVideoReloadNonce((value) => value + 1);
  }, []);

  // Load the one full-video workspace. Parent snapshot updates never trigger a reload.
  useEffect(() => {
    if (!isOpen || !result?.video_id) {
      return undefined;
    }

    const videoId = result.video_id;
    const searchResultRunId = currentResultRunId;
    const restoredSnapshot = persistedSnapshotRef.current;
    if (restoredSnapshot?.videoSegments.length && restoredSnapshot.availableRuns.length && fullVideoReloadNonce === 0) {
      prevRunIdRef.current = restoredSnapshot.selectedRunId;
      return undefined;
    }
    const preferredSegmentId = currentReferenceSegmentId;
    const requestToken = ++fullVideoLoadTokenRef.current;
    let cancelled = false;

    const loadVideoContent = async () => {
      setIsLoadingVideoSegments(true);
      setVideoSegmentsError(null);
      setPromptRunsError(null);
      try {
        const restoredRunId = selectedRunIdRef.current;
        // A validated snapshot may retain an explicitly selected alternate
        // extraction run. Legacy or mismatched snapshots still fail closed to
        // the result's authoritative run.
        const knownRunId = restoredSnapshot && restoredRunId
          ? restoredRunId
          : searchResultRunId || restoredRunId;
        // Observe failures immediately. A known run can finish loading its
        // segments before this request settles, so a raw promise here can
        // otherwise surface as an unhandled rejection in the browser.
        const runsPromise = segmentCache.loadPromptRuns(videoId, false)
          .then((runs) => ({ runs: runs || [], error: null as unknown }))
          .catch((error: unknown) => ({ runs: [] as PromptRun[], error }));
        let runId = knownRunId;
        if (!runId) {
          const runsOutcome = await runsPromise;
          if (cancelled || requestToken !== fullVideoLoadTokenRef.current) return;
          if (runsOutcome.error) {
            throw runsOutcome.error;
          }
          const nextAvailableRuns = runsOutcome.runs;
          runId = resolvePreferredRunId(null, null, nextAvailableRuns);
          setAvailableRuns(nextAvailableRuns);
        }
        selectedRunIdRef.current = runId;
        setSelectedRunId(runId);
        prevRunIdRef.current = runId;
        const retainedSegments = fullVideoReloadNonce === 0 && restoredSnapshot?.selectedRunId === runId
          ? restoredSnapshot.videoSegments
          : null;
        const segments = retainedSegments?.length
          ? retainedSegments
          : await segmentCache.loadSegments(videoId, runId, false);
        if (cancelled || requestToken !== fullVideoLoadTokenRef.current) return;

        const normalizedSegments = normalizeSearchVideoSegments(segments || []);
        const nextState = retainedSegments?.length && restoredSnapshot
          ? {
              videoSegments: normalizedSegments,
              selectedVideoSegment: normalizedSegments.find(segment => segment.segment_id === restoredSnapshot.selectedVideoSegmentId) || null,
              globalTime: restoredSnapshot.globalTime,
              videoDuration: restoredSnapshot.videoDuration,
            }
          : applyLoadedVideoSegmentState(normalizedSegments, preferredSegmentId);
        setVideoSegmentsError(null);
        setIsLoadingVideoSegments(false);

        let nextAvailableRuns: PromptRun[] = [];
        const runsOutcome = await runsPromise;
        if (cancelled || requestToken !== fullVideoLoadTokenRef.current) return;
        if (!runsOutcome.error) {
          nextAvailableRuns = runsOutcome.runs;
          setAvailableRuns(nextAvailableRuns);
        } else {
          console.error('Failed to load extraction runs:', runsOutcome.error);
          setPromptRunsError('Failed to load extraction runs. Video playback is still available.');
        }

        persistFullVideoSnapshot({
          selectedRunId: runId,
          videoSegments: nextState.videoSegments,
          availableRuns: nextAvailableRuns,
          selectedVideoSegmentId: nextState.selectedVideoSegment?.segment_id || null,
          globalTime: nextState.globalTime,
          isPlaying: false,
          videoDuration: nextState.videoDuration,
        });
      } catch (error) {
        if (cancelled || requestToken !== fullVideoLoadTokenRef.current) return;
        console.error('Failed to load video content:', error);
        setVideoSegments([]);
        setPreparedSegments([]);
        setSelectedVideoSegment(null);
        setGlobalTime(0);
        setVideoDuration(0);
        setIsPlaying(false);
        setVideoSegmentsError('Failed to load full video segments. Please try again.');
      } finally {
        if (!cancelled && requestToken === fullVideoLoadTokenRef.current) {
          setIsLoadingVideoSegments(false);
        }
      }
    };

    void loadVideoContent();

    return () => {
      cancelled = true;
    };
  }, [
    applyLoadedVideoSegmentState,
    currentReferenceSegmentId,
    currentResultRunId,
    fullVideoReloadNonce,
    isOpen,
    persistFullVideoSnapshot,
    result?.video_id,
  ]);

  // Load the selected extraction run through the same run-scoped segment cache.
  useEffect(() => {
    if (!isOpen || !result?.video_id) {
      prevRunIdRef.current = selectedRunId;
      return undefined;
    }

    // The initial video-load effect publishes the result's known run before
    // React commits that state. Do not let this effect replay the previous
    // render's run and cancel the authoritative request in the same commit.
    if (selectedRunId !== selectedRunIdRef.current) {
      return undefined;
    }

    if (prevRunIdRef.current === null || prevRunIdRef.current === selectedRunId) {
      prevRunIdRef.current = selectedRunId;
      return undefined;
    }

    const videoId = result.video_id;
    const runId = selectedRunId;
    const preferredSegmentId = selectedVideoSegment?.segment_id || currentReferenceSegmentId;
    const requestToken = ++fullVideoLoadTokenRef.current;
    prevRunIdRef.current = selectedRunId;
    let cancelled = false;

    const loadSegmentsForRun = async () => {
      setIsLoadingVideoSegments(true);
      setVideoSegmentsError(null);
      try {
        const segments = await segmentCache.loadSegments(videoId, runId, false);
        if (cancelled || requestToken !== fullVideoLoadTokenRef.current) return;

        const normalizedSegments = normalizeSearchVideoSegments(segments || []);
        const nextState = applyLoadedVideoSegmentState(normalizedSegments, preferredSegmentId);
        setVideoSegmentsError(null);
        persistFullVideoSnapshot({
          selectedRunId: runId,
          videoSegments: nextState.videoSegments,
          availableRuns,
          selectedVideoSegmentId: nextState.selectedVideoSegment?.segment_id || null,
          globalTime: nextState.globalTime,
          isPlaying: false,
          videoDuration: nextState.videoDuration,
        });
      } catch (error) {
        if (cancelled || requestToken !== fullVideoLoadTokenRef.current) return;
        console.error('Failed to load segments for run:', error);
        setVideoSegments([]);
        setPreparedSegments([]);
        setSelectedVideoSegment(null);
        setGlobalTime(0);
        setVideoDuration(0);
        setIsPlaying(false);
        setVideoSegmentsError('Failed to load full video segments for this extraction run. Please try again.');
      } finally {
        if (!cancelled && requestToken === fullVideoLoadTokenRef.current) {
          setIsLoadingVideoSegments(false);
        }
      }
    };

    void loadSegmentsForRun();

    return () => {
      cancelled = true;
    };
  }, [
    applyLoadedVideoSegmentState,
    availableRuns,
    currentReferenceSegmentId,
    isOpen,
    persistFullVideoSnapshot,
    result?.video_id,
    selectedRunId,
    selectedVideoSegment?.segment_id,
  ]);

  // Sync selected segment with global time changes
  useEffect(() => {
    if (videoSegments.length > 0 && preparedSegments.length > 0) {
      const currentPreparedSegment = locateGlobalTime(globalTime, preparedSegments).segment;
      const currentSegment = videoSegments.find(segment => segment.segment_id === currentPreparedSegment.id);

      if (currentSegment && currentSegment.segment_id !== selectedVideoSegment?.segment_id) {
        setSelectedVideoSegment(currentSegment);
      }
    }
  }, [globalTime, preparedSegments, selectedVideoSegment?.segment_id, videoSegments]);

  // Fetch video title when result changes
  useEffect(() => {
    if (!isOpen) {
      videoTitleLoadTokenRef.current += 1;
      setVideoTitle('');
      return undefined;
    }
    if (!result?.video_id) {
      videoTitleLoadTokenRef.current += 1;
      setVideoTitle('');
      return undefined;
    }

    if (isResolvedVideoTitle(result.video_id, result.video_name)) {
      videoTitleLoadTokenRef.current += 1;
      setVideoTitle(result.video_name as string);
      return undefined;
    }

    const requestToken = ++videoTitleLoadTokenRef.current;
    const videoId = result.video_id;
    const controller = new AbortController();
    let cancelled = false;
    setVideoTitle(videoId);

    const fetchTitle = async () => {
      try {
        const videos = await apiService.getBatchVideoSummaries([videoId], controller.signal);
        if (!cancelled && requestToken === videoTitleLoadTokenRef.current) {
          const video = videos.find((candidate) => candidate.video_id === videoId);
          setVideoTitle(video?.title || videoId);
        }
      } catch (error: any) {
        if (
          !cancelled
          && requestToken === videoTitleLoadTokenRef.current
          && error?.name !== 'AbortError'
          && error?.code !== 'ERR_CANCELED'
        ) {
          console.warn('Failed to fetch video title:', error);
          setVideoTitle(videoId);
        }
      }
    };
    void fetchTitle();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isOpen, result?.video_id, result?.video_name]);

  // Toggle video-level nested path
  const toggleVideoLevelMetadataPath = useCallback((path: string) => {
    setCollapsedVideoLevelPaths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  }, []);

  // Get selected run info
  const selectedRun = useMemo(() => {
    const runId = selectedRunId || currentResultRunId;
    return availableRuns.find(run => run.run_id === runId)
      || Object.values(indexRuns).flat().find(run => run.run_id === runId)
      || null;
  }, [availableRuns, currentResultRunId, indexRuns, selectedRunId]);

  // Check if selected run has video-level enabled
  const selectedRunHasVideoLevel = Boolean(selectedRun?.video_level_enabled);

  // Accepted metadata and derivative publication have one native stream owner.
  // Updating annotations never resets the current playback clock or playing state.
  useEffect(() => {
    if (!isPresentationVisible || !result?.video_id || !selectedRunId) return undefined;
    const videoId = result.video_id; const runId = selectedRunId;
    let cancelled = false; let loading = false; let revision = ''; let videoRevision: unknown;
    const refresh = async () => {
      if (loading) return;
      loading = true;
      try {
        let requested: string;
        do {
          requested = revision;
          const rows = normalizeSearchVideoSegments(await segmentCache.loadSegments(videoId, runId, false));
          if (cancelled) return;
          setVideoSegments(rows);
          const prepared = prepareSegments(rows, videoId);
          setPreparedSegments(current => arePlaybackSegmentsEquivalent(current, prepared) ? current : prepared);
          setVideoDuration(getTotalDuration(prepared));
          setSelectedVideoSegment(current => rows.find(row => row.segment_id === current?.segment_id) || rows[0] || null);
        } while (!cancelled && requested !== revision);
      } catch (error) { if (!cancelled) console.warn('Failed to refresh accepted extraction', error); }
      finally { loading = false; }
    };
    const stop = processingRealtimeStore.subscribe(event => {
      if (event.video_id !== videoId) return;
      const entry = processingRealtimeStore.getVideoState(videoId)?.processing_status?.find(row => row.run_id === runId);
      if (!entry?.result_revisions) return;
      const next = resultRevisionKey(entry.result_revisions, ['metadata', 'segments', 'assets', 'image_index']);
      if (next !== revision) { revision = next; void refresh(); }
      if (entry.result_revisions.video_level !== videoRevision) {
        videoRevision = entry.result_revisions.video_level;
        videoLevelSnapshotRef.current = { ...videoLevelSnapshotRef.current, loaded: false };
        setVideoLevelRevision(current => current + 1);
      }
    }, { video_ids: [videoId], run_ids: [runId] });
    return () => { cancelled = true; stop(); };
  }, [isPresentationVisible, result?.video_id, selectedRunId]);

  // Load video-level result when in video mode with a selected run that has video-level enabled
  useEffect(() => {
    if (!isOpen || !result?.video_id || !selectedRunId) {
      videoLevelLoadTokenRef.current += 1;
      setIsLoadingVideoLevelResult(false);
      return undefined;
    }

    if (!selectedRunHasVideoLevel) {
      videoLevelLoadTokenRef.current += 1;
      setVideoLevelResult(null);
      setVideoLevelResultError(null);
      setIsLoadingVideoLevelResult(false);
      videoLevelSnapshotRef.current = {
        result: null,
        runId: selectedRunId,
        error: null,
        loaded: false,
      };
      return undefined;
    }

    const cachedSnapshot = videoLevelSnapshotRef.current;
    if (cachedSnapshot.runId === selectedRunId && cachedSnapshot.loaded) {
      videoLevelLoadTokenRef.current += 1;
      setVideoLevelResult(cachedSnapshot.result);
      setVideoLevelResultError(cachedSnapshot.error);
      setIsLoadingVideoLevelResult(false);
      return undefined;
    }

    const requestToken = ++videoLevelLoadTokenRef.current;
    const videoId = result.video_id;
    const runId = selectedRunId;
    const controller = new AbortController();
    let cancelled = false;

    const loadVideoLevelResult = async () => {
      setIsLoadingVideoLevelResult(true);
      setVideoLevelResult(null);
      setVideoLevelResultError(null);
      publishVideoLevelSnapshot({ result: null, runId, error: null, loaded: false });

      try {
        const videoLevelRes = await apiService.getPromptRunVideoResult(runId, videoId, controller.signal);
        if (cancelled || controller.signal.aborted || requestToken !== videoLevelLoadTokenRef.current) return;
        setVideoLevelResult(videoLevelRes);
        publishVideoLevelSnapshot({ result: videoLevelRes, runId, error: null, loaded: true });
      } catch (error: any) {
        if (
          cancelled
          || controller.signal.aborted
          || requestToken !== videoLevelLoadTokenRef.current
          || error?.name === 'AbortError'
          || error?.code === 'ERR_CANCELED'
        ) return;
        if (error?.response?.status === 404) {
          setVideoLevelResult(null);
          setVideoLevelResultError(null);
          publishVideoLevelSnapshot({ result: null, runId, error: null, loaded: true });
        } else {
          const message = error?.response?.data?.detail || 'Failed to load video-level output';
          setVideoLevelResult(null);
          setVideoLevelResultError(message);
          // Transient failures remain retryable after a route remount.
          publishVideoLevelSnapshot({ result: null, runId, error: message, loaded: false });
        }
      } finally {
        if (
          !cancelled
          && !controller.signal.aborted
          && requestToken === videoLevelLoadTokenRef.current
        ) {
          setIsLoadingVideoLevelResult(false);
        }
      }
    };

    void loadVideoLevelResult();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    isOpen,
    publishVideoLevelSnapshot,
    result?.video_id,
    selectedRunHasVideoLevel,
    selectedRunId,
    videoLevelRevision,
  ]);

  useEffect(() => {
    setCollapsedMetadataPaths(new Set());
    setDownloadError(null);
  }, [currentModalResultKey]);

  useEffect(() => {
    setDownloadError(null);
  }, [selectedRunId, selectedVideoSegment?.segment_id]);

  const handleClose = useCallback(() => {
    publishPendingFullVideoSnapshot(true);
    onClose();
  }, [onClose, publishPendingFullVideoSnapshot]);

  const commitSidebarWidth = useCallback((width: number) => {
    localStorage.setItem('searchSidebarWidth', width.toString());
    window.dispatchEvent(new CustomEvent('searchSidebarWidthChange', {
      detail: { width },
    }));
  }, []);

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = sidebarWidth;
  };

  useEffect(() => {
    if (isResizing && isPresentationVisible) {
      document.body.classList.add('resizing-search-sidebar');
    } else {
      document.body.classList.remove('resizing-search-sidebar');
    }

    if (!isResizing || !isPresentationVisible) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = resizeStartX.current - e.clientX;
      const newWidth = Math.max(400, Math.min(1400, resizeStartWidth.current + deltaX));
      if (newWidth === sidebarWidthRef.current) return;
      sidebarWidthRef.current = newWidth;
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      commitSidebarWidth(sidebarWidthRef.current);
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.classList.remove('resizing-search-sidebar');
    };
  }, [commitSidebarWidth, isPresentationVisible, isResizing]);

  // Bounded stream URLs are opaque and intentionally omit the object suffix,
  // so prefer the authoritative backend media type and retain the durable
  // source as a compatibility signal for derived image assets.
  const isImage = Boolean(
    result
    && (
      result.media_type === 'image'
      || [result.gcs_uri, getSegmentPlaybackSource(result), selectedVideoSegment?.gcs_uri, selectedVideoSegment?.segment_uri]
        .some((uri) => Boolean(uri && isImageFile(uri)))
    ),
  );

  const selectedIsOriginal = selectedVideoSegment?.segment_id === currentReferenceSegmentId
    && selectedRunId === currentResultRunId;
  const imageSource = pickMediaUri(
    selectedIsOriginal || !selectedVideoSegment ? getSegmentPlaybackSource(result || {}) : null,
    getSegmentPlaybackSource(selectedVideoSegment || {}),
  );
  const imageAuthority = pickDurableMediaUri(
    selectedVideoSegment?.gcs_uri,
    selectedIsOriginal || !selectedVideoSegment ? result?.gcs_uri : null,
  );
  const {
    resolvedUrl: imageUrl,
    isLoading: isLoadingImage,
    refreshAfterError: refreshImageAfterError,
  } = useResolvedMediaUrl(
    isOpen && isImage ? imageSource : null,
    {
      durableSource: imageAuthority,
      videoId: result?.video_id,
      segmentId: selectedVideoSegment?.segment_id || currentReferenceSegmentId,
    },
  );

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Download segment metadata as JSON
  const handleDownloadSegmentMetadata = useCallback(() => {
    if (!result) return;
    const exportLabel = result.result_type === 'video' ? 'video_result' : 'segment';
    const durablePlaybackUri = pickDurableMediaUri(
      result.segment_uri,
      result.gcs_uri,
      result.result_type === 'video' ? result.video_uri : null,
    );
    const durableMatchedImageUri = pickDurableMediaUri(
      result.matched_image_uri,
      result.matched_image_gcs_uri,
    );

    const exportData = {
      export_info: {
        exported_at: new Date().toISOString(),
        result_type: result.result_type,
        result_id: result.result_id,
        video_id: result.video_id,
        video_name: videoTitle || result.video_name || null,
        segment_id: result.segment_id,
        run_id: getSearchResultRunId(result),
        index_id: result.source_index_id || null,
      },
      result: {
        result_type: result.result_type,
        result_id: result.result_id,
        segment_id: result.segment_id,
        video_id: result.video_id,
        video_name: videoTitle || result.video_name || null,
        start_time: displayStartTime,
        end_time: displayEndTime,
        duration: displayDuration,
        segment_uri: durablePlaybackUri,
        run_id: getSearchResultRunId(result),
        index_id: result.source_index_id || null,
        text_content: result.text_content || null,
        metadata_text: result.metadata_text || null,
        metadata: result.extracted_metadata || result.metadata || {},
        field_scores: result.field_scores || {},
        field_instance_scores: result.field_instance_scores || {},
        matched_field_paths: result.matched_field_paths || [],
        matched_field_instances: result.matched_field_instances || [],
        matched_image_gcs_uri: result.matched_image_gcs_uri || null,
        matched_image_uri: durableMatchedImageUri,
        matched_image_timestamp: result.matched_image_timestamp ?? null,
        shot_timestamp: result.shot_timestamp ?? null,
      }
    };

    const videoName = videoTitle || result.video_name || result.video_id;
    const safeVideoName = videoName.replace(/[^a-zA-Z0-9-_]/g, '_').substring(0, 50);
    const entityKey = (result.segment_id || result.result_id || result.video_id).replace(/[^a-zA-Z0-9-_]/g, '_').substring(0, 24);
    downloadJsonFile(exportData, `${safeVideoName}_${exportLabel}_${entityKey}_metadata.json`);
  }, [displayDuration, displayEndTime, displayStartTime, result, videoTitle]);

  // Handle segment click in video view
  const handleVideoSegmentClick = useCallback((segment: SegmentData) => {
    setSelectedVideoSegment(segment);
    // Find the prepared segment to get correct global time
    const preparedSeg = preparedSegments.find((s: VideoSegment) => s.id === segment.segment_id);
    if (preparedSeg) {
      handleFullVideoTimeChange(preparedSeg.start);
    }
    handleFullVideoPlayingChange(true);
  }, [handleFullVideoPlayingChange, handleFullVideoTimeChange, preparedSegments]);

  if (!result) return null;

  // Show only explicit field scores and image-search scores; generic ranking scores are not user-facing confidence values.
  const headerImageScore = getImageSearchScore(result);

  return (
    <>
      <ResultDetailSidebar
        ref={modalRef}
        isOpen={isOpen}
        isVisible={isActive && isVisible}
        onClose={handleClose}
        label="Video details"
        width={sidebarWidth}
        isResizing={isResizing}
        onResizeStart={handleResizeStart}
      >
        <header className="search-result-header">
          <div className="search-result-header__main">
            <h3 className="search-result-header__title">{videoTitle || 'Loading video…'}</h3>
            <div className="search-result-header__actions">
              <FeatureAction feature="export_lifecycle"><button type="button" className="export-segment-btn" onClick={handleDownloadSegmentMetadata} title="Export search result metadata as JSON">
                <Download size={14} />
                Export
              </button></FeatureAction>
              <button type="button" className="close-button" onClick={handleClose} aria-label="Close video details">
                <X size={20} />
              </button>
            </div>
          </div>

          <dl className="search-result-header__context">
            <div className="search-result-header__field">
              <dt>Index</dt>
              <dd>{indexInfo?.name || 'Unavailable'}</dd>
            </div>
            <div className="search-result-header__field">
              <dt>Segmentation</dt>
              <dd>{formatRunSegmentationLabel(selectedRun, result.media_type) || (isLoadingVideoSegments ? 'Loading…' : 'Unavailable')}</dd>
            </div>
          </dl>
          {headerImageScore !== null && headerImageScore > 0 && (
            <span className="search-score-chip">Image score: {formatSearchScore(headerImageScore)}</span>
          )}
        </header>

        {/* Full-video workspace */}
        <div className="view-container">
            <div className="modal-content video-view">
              {promptRunsError && (
                <div className="search-result-notice" role="alert">
                  <AlertCircle size={20} />
                  <span>{promptRunsError}</span>
                  <button type="button" onClick={handleRetryFullVideoLoad}>Retry</button>
                </div>
              )}
              {/* Extraction Run Selector */}
              {availableRuns.length > 0 && (
                <div className="run-selector-section">
                  <div className="run-selector-row">
                    <div className="custom-select-container" ref={runDropdownRef}>
                      <button
                        type="button"
                        className="custom-select-trigger"
                        onClick={() => setShowRunDropdown(!showRunDropdown)}
                      >
                        <span className="select-value">
                          {getRunDisplayName(selectedRunId, availableRuns)}
                        </span>
                        {showRunDropdown ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>

                      {showRunDropdown && (
                        <div className="custom-dropdown">
                          <div className="dropdown-header">
                            <span className="dropdown-title">Select Extraction Run</span>
                          </div>
                          <div className="dropdown-list">
                            {availableRuns.map(run => {
                              const isSelected = selectedRunId === run.run_id;
                              const runResultCount = getRunResultCount(run.run_id);
                              const getStatusIcon = (status: boolean | null | undefined) => {
                                if (status === null || status === undefined) return <Clock size={12} className="status-pending" />;
                                if (status === true) return <CheckCircle size={12} className="status-success" />;
                                return <AlertCircle size={12} className="status-failed" />;
                              };
                              return (
                                <div
                                  key={run.run_id}
                                  className={`dropdown-item ${isSelected ? 'selected' : ''} ${runResultCount > 0 ? 'has-results' : ''}`}
                                  onClick={() => {
                                    publishPendingFullVideoSnapshot(true);
                                    selectedRunIdRef.current = run.run_id;
                                    setSelectedRunId(run.run_id);
                                    setShowRunDropdown(false);
                                  }}
                                >
                                  <div className="dropdown-item-content">
                                    <div className="dropdown-item-name-row">
                                      <span className="field-name">{run.prompt_name}</span>
                                      {runResultCount > 0 && (
                                        <span className="run-result-count" title={`${runResultCount} search result${runResultCount !== 1 ? 's' : ''} from this extraction run`}>
                                          {runResultCount} result{runResultCount !== 1 ? 's' : ''}
                                        </span>
                                      )}
                                    </div>
                                    <div className="dropdown-status-icons">
                                      <span className="status-badge" title="Field Extraction">
                                        {getStatusIcon(run.field_extraction_succeeded)}
                                        <span className="status-label">Fields</span>
                                      </span>
                                    </div>
                                  </div>
                                  <span className="run-date">
                                    {new Date(run.executed_at).toLocaleDateString()}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {selectedRunId && (
                      <div className="run-selector-marker" onClick={(e) => e.stopPropagation()}>
                        <MarkerEditor
                          marker={availableRuns.find(r => r.run_id === selectedRunId)?.marker}
                          target={{
                            entity_type: 'prompt_run',
                            index_id: indexIdForFieldMarkers || undefined,
                            run_id: selectedRunId,
                            video_id: result?.video_id || undefined,
                          }}
                          onMarkerChange={(next) => handlePromptRunMarkerChange(selectedRunId, next)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {downloadError && (
                <div className="search-result-notice" role="alert">
                  {downloadError}
                  <button type="button" onClick={() => setDownloadError(null)} aria-label="Dismiss download error"><X size={14} /></button>
                </div>
              )}
              {isImage && (
                <div className="search-result-image-section">
                  {imageUrl ? (
                    <img src={imageUrl} alt="Search result" onError={() => refreshImageAfterError(imageUrl)} />
                  ) : isLoadingImage ? (
                    <div className="loading-state"><RefreshCw size={20} className="spinning" />Loading image...</div>
                  ) : (
                    <div className="search-result-notice"><AlertCircle size={20} />Image unavailable</div>
                  )}
                </div>
              )}
              {isImage && videoSegmentsError && (
                <div className="search-result-notice" role="alert">
                  {videoSegmentsError}
                  <button type="button" onClick={handleRetryFullVideoLoad}>Retry</button>
                </div>
              )}

              {/* Segmented Video Player */}
              {!isImage && (
                <div className="segmented-video-section">
                  {isLoadingVideoSegments ? (
                    <div className="loading-state">
                      <RefreshCw size={20} className="spinning" />
                      <span>Loading full video...</span>
                    </div>
                  ) : videoSegmentsError ? (
                    <div className="full-video-error">
                      <AlertCircle size={28} />
                      <span>{videoSegmentsError}</span>
                      <button type="button" onClick={handleRetryFullVideoLoad}>
                        Retry
                      </button>
                    </div>
                  ) : preparedSegments.length > 0 ? (
                    <>
                      <SegmentedPlayer
                        segments={preparedSegments}
                        globalTime={globalTime}
                        onGlobalTimeChange={handleFullVideoTimeChange}
                        playing={isPresentationVisible && isPlaying}
                        muted={false}
                        volume={0.8}
                        onPlayingChange={handleFullVideoPlayingChange}
                        onError={(err) => console.error('Player error:', err)}
                      />
                      <SegmentedTimeline
                        segments={preparedSegments}
                        globalTime={globalTime}
                        totalDuration={getTotalDuration(preparedSegments)}
                        onChange={handleFullVideoTimeChange}
                      />
                    </>
                  ) : (
                    <div className="no-segments">
                      <AlertCircle size={48} />
                      <p>No video segments available</p>
                    </div>
                  )}
                </div>
              )}

              {/* Timeline with GIFs Section */}
              {videoSegments.length > 0 && (
                <div className="timeline-with-gifs-section">
                  <div className="search-segment-filter" role="group" aria-label="Segment filter">
                    <button type="button" className={!showMatchingSegmentsOnly ? 'active' : ''}
                      aria-pressed={!showMatchingSegmentsOnly} onClick={() => setShowMatchingSegmentsOnly(false)}>
                      All segments <span>{videoSegments.length}</span>
                    </button>
                    <button type="button" className={showMatchingSegmentsOnly ? 'active' : ''}
                      aria-pressed={showMatchingSegmentsOnly} onClick={() => setShowMatchingSegmentsOnly(true)}>
                      Search results <span>{matchingVideoSegments.length}</span>
                    </button>
                  </div>
                  <div className="timeline-container">
                    <SegmentPreviewGallery ariaLabel="Video segment previews" emptyState="No search-result segments in this extraction run.">
                      {displayedVideoSegments.map((segment) => {
                        const fieldScore = highestFieldScoresBySegmentId.get(segment.segment_id);
                        return (
                          <SegmentPreviewGalleryItem
                            key={segment.segment_id}
                            selected={selectedVideoSegment?.segment_id === segment.segment_id}
                            onSelect={() => handleVideoSegmentClick(segment)}
                            title={`Play segment at ${formatTime(segment.start_time)}`}
                            timeLabel={`${formatTime(segment.start_time)} – ${formatTime(segment.end_time)}`}
                            actions={(
                              <SegmentDownloadActions
                                segment={segment}
                                segmentIndex={videoSegments.indexOf(segment)}
                                videoId={result.video_id}
                                videoTitle={videoTitle || result.video_name || result.video_id}
                                runId={selectedRunId}
                                promptName={selectedRun?.prompt_name}
                                onError={setDownloadError}
                              />
                            )}
                            status={resolveSegmentProcessingStatus(segment)}
                          >
                            <SegmentGifPreview
                              segment={segment}
                              videoId={result.video_id}
                            />
                            {fieldScore !== undefined && (
                              <span className="search-segment-score"
                                title={`Highest field score: ${formatSearchScore(fieldScore)}`}
                                aria-label={`Highest field score ${formatSearchScore(fieldScore)}`}>
                                {formatSearchScore(fieldScore)}
                              </span>
                            )}
                          </SegmentPreviewGalleryItem>
                        );
                      })}
                    </SegmentPreviewGallery>
                  </div>
                </div>
              )}

              {/* Selected Segment Metadata */}
              {selectedVideoSegment && (
                <div className="segment-details">
                  <div className="segment-details-header">
                    <h3>
                      Segment Details ({formatTime(selectedVideoSegment.start_time)} - {formatTime(selectedVideoSegment.end_time)})
                    </h3>
                    {Object.keys(selectedSegmentMetadata).length > 0 && (
                      <MetadataViewControls
                        mode={metadataViewMode}
                        onModeChange={setMetadataViewMode}
                        sortBy={metadataSortBy}
                        sortDirection={metadataSortDir}
                        onSortChange={handleSortChange}
                        allExpanded={allFieldsExpanded}
                        onToggleAll={() => handleToggleAllFields(selectedSegmentMetadata)}
                        showScoreSort
                      />
                    )}
                  </div>

                  {Object.keys(selectedSegmentMetadata).length > 0 ? (
                    <div className="metadata-display">
                      <MetadataValueView
                        mode={metadataViewMode}
                        value={selectedSegmentMetadata}
                        isFieldMatched={segmentFieldMatcher}
                        collapsedPaths={collapsedMetadataPaths}
                        onTogglePath={toggleMetadataPath}
                        getFieldScore={(fieldPath, pathTokens) => {
                          const segmentSearchResult = getSearchResultForSegment(selectedVideoSegment.segment_id);
                          return getBestFieldScoreForDisplayPath(
                            segmentSearchResult?.field_scores || {},
                            segmentSearchResult?.field_instance_scores || {},
                            fieldPath,
                            pathTokens,
                          );
                        }}
                        getFieldPriorityRank={(pathTokens) => getFieldPriorityRank(
                          pathTokens,
                          prioritizedMetadataFieldPathSegments,
                        )}
                        formatFieldScore={formatSearchScore}
                        sortBy={metadataSortBy}
                        sortDirection={metadataSortDir}
                      />
                    </div>
                  ) : selectedVideoSegment.processed ? (
                    <div className="metadata-display">
                      <p className="metadata-empty">No metadata available for this segment</p>
                    </div>
                  ) : (
                    <div className="metadata-display">
                      <div className="loading-state">
                        <RefreshCw size={16} className="spinning" />
                        <span>Processing segment metadata...</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Video-Level Output Panel */}
              {selectedRunHasVideoLevel && (
                <div className="video-level-panel">
                  <div className="video-level-header">
                    <span>Video-Level Output</span>
                    {videoLevelResult?.status && (
                      <span className={`video-level-status-badge status-${videoLevelResult.status.toLowerCase()}`}>
                        {videoLevelResult.status}
                      </span>
                    )}
                  </div>

                  {isLoadingVideoLevelResult && !videoLevelResult ? (
                    <div className="video-level-loading">
                      <RefreshCw size={14} className="spinning" />
                      <span>Loading video-level output...</span>
                    </div>
                  ) : videoLevelResultError ? (
                    <div className="video-level-error">
                      <AlertCircle size={14} />
                      <span>{videoLevelResultError}</span>
                    </div>
                  ) : videoLevelResult?.status === 'successful' ? (
                    <div className="video-level-content">
                      {videoLevelResult.processing_warning && (
                        <div className="video-level-note">
                          {videoLevelResult.processing_warning}
                        </div>
                      )}
                      <div className="video-level-block">
                        <div className="video-level-block-header">
                          <div className="video-level-block-title">Structured Metadata</div>
                          <div className="view-mode-toggle">
                            <button
                              className={`view-mode-btn ${videoLevelViewMode === 'fields' ? 'active' : ''}`}
                              onClick={() => setVideoLevelViewMode('fields')}
                              title="Graphical view"
                            >
                              <List size={12} />
                            </button>
                            <button
                              className={`view-mode-btn ${videoLevelViewMode === 'json' ? 'active' : ''}`}
                              onClick={() => setVideoLevelViewMode('json')}
                              title="Raw JSON view"
                            >
                              <Code size={12} />
                            </button>
                          </div>
                        </div>
                        {videoLevelViewMode === 'json' ? (
                          <pre className="video-level-json">
                            {JSON.stringify(videoLevelResult.metadata || {}, null, 2)}
                          </pre>
                        ) : (
                          <div className="video-level-fields">
                            {Object.keys(videoLevelResult.metadata || {}).length > 0 ? (
                              <StructuredMetadataView
                                value={videoLevelResult.metadata}
                                isFieldMatched={videoFieldMatcher}
                                collapsedPaths={collapsedVideoLevelPaths}
                                onTogglePath={toggleVideoLevelMetadataPath}
                                getFieldScore={selectedVideoSearchResult
                                  ? (fieldPath, pathTokens) => getBestFieldScoreForDisplayPath(
                                    selectedVideoSearchResult.field_scores || {},
                                    selectedVideoSearchResult.field_instance_scores || {},
                                    fieldPath,
                                    pathTokens,
                                  )
                                  : undefined}
                                getFieldPriorityRank={(pathTokens) => getFieldPriorityRank(
                                  pathTokens,
                                  prioritizedMetadataFieldPathSegments,
                                )}
                                formatFieldScore={formatSearchScore}
                                sortBy={metadataSortBy}
                                sortDirection={metadataSortDir}
                              />
                            ) : (
                              <div className="video-level-empty-fields">No structured metadata</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : videoLevelResult ? (
                    <div className="video-level-empty">
                      {videoLevelResult.error_message || 'Video-level processing failed or is still in progress.'}
                    </div>
                  ) : (
                    <div className="video-level-empty">
                      No video-level output available yet.
                    </div>
                  )}
                </div>
              )}
            </div>
        </div>
      </ResultDetailSidebar>
    </>
  );
};

export default SearchResultDetailModal;
